
// ─── AIChatPill ───────────────────────────────────────────────────────────────

import styled from 'styled-components';
import { Button } from '../button';
import { Icon } from '../icon';
import { Input } from '../form/input';

const PILL_HEIGHT = 40;

export const PillCheckbox = styled(Input)`
	&& {
		position: absolute;
		inset: 0;
		appearance: none;
		cursor: pointer;
		z-index: 9;
		border-radius: 2px;
		width: auto;
		height: auto;
		border: none;
		box-shadow: none;
		background: transparent;
		padding: 0;

		&:checked {
			pointer-events: none;
		}

		&:focus {
			border: none;
			box-shadow: none;
		}

		&:disabled {
			cursor: not-allowed;
		}
	}
`;

export const PillSparklesIcon = styled(Icon)`
	font-size: 14px;
	color: ${props => props.theme.aiChat.pill.color};
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	flex-shrink: 0;
	transition: color 0.5s ease-in-out;
`;

export const PillLabel = styled.span`
	font-family: ${props => props.theme.font};
	font-size: 13px;
	font-weight: 500;
	color: ${props => props.theme.aiChat.pill.color};
	white-space: nowrap;
	overflow: hidden;
	flex-shrink: 0;
	max-width: 200px;
	opacity: 1;
	padding-right: 8px;
	transition: max-width 0.5s ease-in-out, padding-right 0.5s ease-in-out, opacity 0.5s ease-in-out;
`;

export const PillInput = styled(Input)`
	&& {
		flex: 1;
		min-width: 0;
		width: 0;
		opacity: 0;
		border: none;
		box-shadow: none;
		background: transparent;
		color: ${props => props.theme.aiChat.input.color};
		height: auto;
		padding: 0;
		overflow: hidden;
		transition: width 0.5s ease-in-out, padding 0.5s ease-in-out, opacity 0.5s ease-in-out;

		&::placeholder {
			color: ${props => props.theme.aiChat.pill.placeholder};
		}

		&:focus {
			border: none;
			box-shadow: none;
		}

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
			background: transparent;
		}
	}
`;

export const PillActions = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	overflow: hidden;
	max-width: 0;
	opacity: 0;
	flex-shrink: 0;
	transition: max-width 0.5s ease-in-out, opacity 0.5s ease-in-out;
`;

export const PillCloseButton = styled(Button)`
	&& {
		width: 32px;
		height: 36px;
		background: transparent;
		border: none;
		color: ${props => props.theme.aiChat.input.placeholder};
		font-size: 14px;
		flex-shrink: 0;

		${Icon.cssSelector} {
			color: inherit;
		}

		&:hover:not(:disabled) {
			color: ${props => props.theme.aiChat.input.color};
			background: ${props => props.theme.aiChat.messageList.background};

			${Icon.cssSelector} {
				color: inherit;
			}
		}

		&:focus-visible {
			outline: none;
			box-shadow: ${props => props.theme.aiChat.input.focusShadow};
		}

		&:disabled {
			cursor: not-allowed;
		}
	}
`;

export const PillSendButton = styled(Button)`
	&& {
		width: 32px;
		height: 32px;
		border-radius: 4px;
		border: none;
		background: ${props => props.theme.aiChat.pill.sendBackground};
		color: ${props => props.theme.aiChat.pill.sendColor};
		font-size: 14px;
		flex-shrink: 0;

		${Icon.cssSelector} {
			color: inherit;
		}

		&:hover:not(:disabled) {
			background: ${props => props.theme.aiChat.pill.sendBackgroundHover};

			${Icon.cssSelector} {
				color: inherit;
			}
		}

		&:disabled {
			background: ${props => props.theme.aiChat.card.border};
			color: ${props => props.theme.aiChat.pill.placeholder};
			cursor: not-allowed;
			opacity: 1;
		}

		&:focus-visible {
			outline: none;
			box-shadow: ${props => props.theme.aiChat.input.focusShadow};
		}
	}
`;

export const PillMainBox = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	height: ${PILL_HEIGHT}px;
	border-radius: 2px;
	padding: 4px;
	width: fit-content;
	min-width: 0;
	cursor: pointer;
	background: ${props => props.theme.aiChat.pill.background};
	border: 1px solid transparent;
	outline: none;
	transition:
		min-width 0.5s ease-in-out,
		background 0.5s ease-in-out,
		border-color 0.5s ease-in-out,
		box-shadow 0.5s ease-in-out;

	&:focus-visible {
		box-shadow: ${props => props.theme.aiChat.input.focusShadow};
	}

	&:has(${PillCheckbox}:checked) {
		min-width: 100%;
		cursor: default;
		background: ${props => props.theme.aiChat.pill.expandedBackground};
		border-color: ${props => props.theme.aiChat.pill.expandedBorder};
		box-shadow: ${props => props.theme.aiChat.pill.expandedHalo};

		${PillSparklesIcon} {
			color: ${props => props.theme.aiChat.pill.leadingIcon};
		}

		${PillLabel} {
			max-width: 0;
			opacity: 0;
			padding-right: 0;
		}

		${PillInput} {
			width: 260px;
			opacity: 1;
			padding: 0 0 0 8px;
			overflow: visible;
		}

		${PillActions} {
			max-width: 120px;
			opacity: 1;
		}
	}
`;

export const PillRoot = styled.div`
	position: relative;
	display: flex;
	justify-content: center;
	width: 100%;
	height: ${PILL_HEIGHT}px;
`;
